import React from 'react';
import type { QueryResult } from 'react-apollo';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { DocumentSpaceTitle } from '@confluence/document-title/entry-points/DocumentSpaceTitle.ts';
import { EditContentButton } from '@confluence/edit-button/entry-points/EditContentButton.ts';
import { LoadableAfterPaint } from '@confluence/loadable';
import type {
	SpaceOverviewQueryType,
	SpaceOverviewQueryVariables,
} from '@confluence/space-overview-query';
import { HubStatusLozenge } from '@confluence/company-hub-utils/entry-points/HubStatusLozenge.ts';
import {
	HubSettingsEntryButtonAppearance,
	HubSettingsEntryButton,
} from '@confluence/company-hub-utils/entry-points/HubSettingsEntryButton.ts';

const editButtonStyles = xcss({
	position: 'relative',
	'::after': {
		content: '""',
		position: 'absolute',
		borderWidth: 'border.width',
		borderStyle: 'solid',
		borderRadius: 'radius.small',
		borderColor: 'color.border',
		inset: 'space.0',
		pointerEvents: 'none',
	},
});

// HubPreviewHeader should not overlay the content that follows it when the scroll position is at the top.
// Pulled here out of HubPreviewHeader to avoid vertical content shift given HubPreviewHeader loads lazily.
const fixedPreviewPlaceholderStyles = xcss({
	background: token('color.background.accent.gray.subtlest'), // Match BaseHubPreviewHeader
	height: `59px`, // BREADCRUMB_HEADER_HEIGHT like FixedSpaceHeaderContainer used by SpaceOverviewGroupedButtons, but lint doesn't allow using an import or a variable
});

const fixedPlaceholderStyles = xcss({
	height: '48px',
	containerType: 'inline-size',
});

// CompanyHubGroupedButtons should stick to the top of the content area
const headerContainerStyles = xcss({
	justifyContent: 'space-between',
	alignItems: 'center',
	width: '100cqw',
	height: '48px',
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
	borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	// @ts-ignore because none of the allowed contants fit the use case
	zIndex: 12, // Like FixedSpaceHeaderContainer used by SpaceOverviewGroupedButtons
	position: 'fixed',
	backgroundColor: 'elevation.surface',
});

export type CompanyHubGroupedButtonsProps = {
	contentId: string;
	spaceKey: string;
	spaceOverviewQueryResult: QueryResult<SpaceOverviewQueryType, SpaceOverviewQueryVariables>;
	versionOverride?: number | null;
};

export const CompanyHubGroupedButtons = ({
	contentId,
	spaceKey,
	spaceOverviewQueryResult,
	versionOverride,
}: CompanyHubGroupedButtonsProps) => {
	const canEdit = getCanEdit(spaceOverviewQueryResult);

	if (versionOverride != null) {
		return (
			<Box xcss={fixedPreviewPlaceholderStyles}>
				<HubPreviewHeader
					canEdit={canEdit}
					contentId={contentId}
					spaceKey={spaceKey}
					versionOverride={versionOverride}
				/>
			</Box>
		);
	}

	if (canEdit) {
		return (
			<Box xcss={[fixedPlaceholderStyles]}>
				<Flex xcss={[headerContainerStyles]}>
					<HubStatusLozenge />
					<Inline space="space.100">
						<Inline xcss={[editButtonStyles]}>
							<EditContentButton contentId={contentId} isCompanyHubButton />
						</Inline>
						<HubSettingsEntryButton appearance={HubSettingsEntryButtonAppearance.ICON_ONLY} />
					</Inline>
				</Flex>
				<DocumentSpaceTitle spaceKey={spaceKey} />
			</Box>
		);
	}

	return null;
};

const getCanEdit = (
	spaceOverviewQueryResult: CompanyHubGroupedButtonsProps['spaceOverviewQueryResult'],
) =>
	Boolean(
		spaceOverviewQueryResult?.data?.space?.homepage?.operations?.some(
			(o) => o?.operation === 'update',
		),
	);

const HubPreviewHeader = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-HubPreviewHeader" */ './HubPreviewHeader.tsx'))
			.HubPreviewHeader,
});
